import { STATUS_META, TASK_STATUSES } from './taskStatus';

export type GroupBy = 'status' | 'project' | 'priority' | 'assignee' | 'due';

export interface BoardTask {
  id: string; title: string; status: string; priority: string;
  due_date: string | null; assignee_id: string | null; assignee_name: string | null;
  scope_ids: string[]; tag_ids: string[]; board_sort_order?: number;
}
export interface BoardCtx {
  scopes: { id: string; name: string; color: string }[];
  people: { id: string; name: string }[];
}
export interface ColumnDef { key: string; label: string; color: string; isNew?: boolean; }
export interface TaskPatch {
  status?: string; priority?: string; assignee_id?: string | null;
  scope_ids?: string[]; due_date?: string | null;
}

const PRIORITY_META: Record<string, { label: string; color: string }> = {
  urgent: { label: 'Urgent', color: '#ef4444' },
  high:   { label: 'High',   color: '#f59e0b' },
  medium: { label: 'Medium', color: '#a0a6be' },
  low:    { label: 'Low',    color: '#636b88' },
};
const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];

const DUE_COLUMNS: ColumnDef[] = [
  { key: 'overdue', label: 'Overdue',   color: '#ef4444' },
  { key: 'today',   label: 'Today',     color: '#f59e0b' },
  { key: 'week',    label: 'This Week', color: '#38bdf8' },
  { key: 'later',   label: 'Later',     color: '#6366f1' },
  { key: 'none',    label: 'No date',   color: '#636b88' },
];

function shift(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

export function getColumns(groupBy: GroupBy, ctx: BoardCtx): ColumnDef[] {
  switch (groupBy) {
    case 'status':
      return TASK_STATUSES.map(k => ({
        key: k, label: STATUS_META[k].label, color: STATUS_META[k].color,
        ...(k === 'next' ? { isNew: true } : {}),
      }));
    case 'priority':
      return PRIORITY_ORDER.map(k => ({ key: k, label: PRIORITY_META[k].label, color: PRIORITY_META[k].color }));
    case 'project':
      return [...ctx.scopes.map(s => ({ key: s.id, label: s.name, color: s.color })),
              { key: 'none', label: 'No project', color: '#636b88' }];
    case 'assignee':
      return [...ctx.people.map(p => ({ key: p.id, label: p.name, color: '#6366f1' })),
              { key: 'none', label: 'Unassigned', color: '#636b88' }];
    case 'due':
      return DUE_COLUMNS;
  }
}

function dueBucket(dueDate: string | null, todayStr: string): string {
  if (!dueDate) return 'none';
  if (dueDate < todayStr) return 'overdue';
  if (dueDate === todayStr) return 'today';
  if (dueDate <= shift(todayStr, 7)) return 'week';
  return 'later';
}

export function columnKeysForTask(task: BoardTask, groupBy: GroupBy, todayStr: string): string[] {
  switch (groupBy) {
    case 'status':   return [task.status];
    case 'priority': return [task.priority];
    case 'assignee': return [task.assignee_id ?? 'none'];
    case 'project':  return task.scope_ids.length ? [...task.scope_ids] : ['none'];
    case 'due':      return [dueBucket(task.due_date, todayStr)];
  }
}

export function representativeDue(bucket: string, todayStr: string): string | null {
  switch (bucket) {
    case 'overdue': return shift(todayStr, -1);
    case 'today':   return todayStr;
    case 'week':    return shift(todayStr, 4);
    case 'later':   return shift(todayStr, 14);
    default:        return null;
  }
}

export function mutationForDrop(
  task: BoardTask, groupBy: GroupBy, fromCol: string | null, toCol: string, todayStr: string,
): TaskPatch {
  switch (groupBy) {
    case 'status':   return { status: toCol };
    case 'priority': return { priority: toCol };
    case 'assignee': return { assignee_id: toCol === 'none' ? null : toCol };
    case 'due':      return { due_date: representativeDue(toCol, todayStr) };
    case 'project': {
      let scopes = task.scope_ids.slice();
      if (fromCol && fromCol !== 'none') scopes = scopes.filter(id => id !== fromCol);
      if (toCol === 'none') scopes = [];
      else if (!scopes.includes(toCol)) scopes.push(toCol);
      return { scope_ids: scopes };
    }
  }
}
